import { Autocomplete, TextField } from "@mui/material";
import { Field } from "formik";
import * as React from "react";
import { requiredResponseValidator } from "../formikContainer/requiredResponseValidator";
import { createMinReqHelperText } from "./createMinReqHelperText";
import { Link, useNavigate } from "react-router-dom";
import { Dispatcher } from "../../pages/Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { WizardContainer } from "../formikContainer/WizardContainer";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { filterCameraProductOptionsByTrioExclusion } from "./cameraProductsMutualExclusion";

export const MultipleChoiceFieldType = ({
  config,
  formik,
  setErrorState,
  setChoicesChanger,
  choicesChanger,
  setHealthCheckSelected,
  setActiveWizardContainer,
  handleSubmitSite,
  responseObject,
  setVisibleScreen,
}: {
  config: any;
  formik: any;
  setErrorState: any;
  setChoicesChanger: any;
  choicesChanger: any;
  setHealthCheckSelected: Dispatcher<boolean>;
  setActiveWizardContainer: any;
  handleSubmitSite: any;
  responseObject: any;
  setVisibleScreen: any;
}) => {
  const options = config.options;
  const optionsIds = new Map();
  const optionNames = new Map();
  options.forEach((option: any) => {
    optionsIds.set(option[0], option[1]);
  });
  options.forEach((option: any) => {
    optionNames.set(option[1], option[0]);
  });
  const [newChoicesChanger, setNewChoicesChanger] = React.useState(false);

  const [helperText, setHelperText] = React.useState(config.helperText);

  const navigate = useNavigate();

  const { isFeatureEnabled } = useFeatureFlag();
  const showLineCrossings = isFeatureEnabled("showLineCrossings", true);

  if (!choicesChanger) {
    choicesChanger = newChoicesChanger;
    setChoicesChanger = setNewChoicesChanger;
  }

  const getFilteredOptions = React.useCallback(
    (options: any[]) => {
      if (config.id === "products" && !showLineCrossings) {
        return options.filter(
          (subArr: [string, string]) => subArr[0] !== "Line Crossing"
        );
      }
      return options;
    },
    [config.id, showLineCrossings]
  );

  const vField = formik.values[config.field];
  const productsSelectionSignature = Array.isArray(vField)
    ? [...(vField as number[])].sort((a, b) => a - b).join(",")
    : "";

  const computeProductChoiceOptions = React.useCallback(
    (selectedDisplayNames: string[]) => {
      const base = getFilteredOptions(config.options);
      if (config.id !== "products" || !location.href.includes("addcamera")) {
        return base;
      }
      return filterCameraProductOptionsByTrioExclusion(base, selectedDisplayNames);
    },
    [config.id, config.options, getFilteredOptions]
  );

  const [choiceOptions, setChoiceOptions] = React.useState(() =>
    computeProductChoiceOptions(
      Array.isArray(formik.values[config.field])
        ? (formik.values[config.field] as number[])
            .map((element: number) => optionNames.get(element))
            .filter((item: string | undefined): item is string => item !== undefined)
        : []
    )
  );

  React.useEffect(() => {
    if (config.id !== "products") {
      return;
    }
    const namesById = new Map<number, string>();
    config.options.forEach((opt: [string, number]) => {
      namesById.set(opt[1], opt[0]);
    });
    const labels =
      Array.isArray(formik.values[config.field]) && namesById.size > 0
        ? (formik.values[config.field] as number[])
            .map((id) => namesById.get(id))
            .filter((x): x is string => x !== undefined)
        : [];
    setChoiceOptions(computeProductChoiceOptions(labels));
  }, [
    config.id,
    config.field,
    config.options,
    computeProductChoiceOptions,
    productsSelectionSignature,
  ]);

  return (
    <div>
      <Autocomplete
        multiple
        disablePortal
        id={config.id}
        defaultValue={
          Array.isArray(formik.values[config.field])
            ? formik.values[config.field]
                .map((element: number) => optionNames.get(element))
                .filter((item: string | undefined) => item !== undefined) // Filter out invalid mappings
            : []
        }
        options={choiceOptions.map((option: string[]) => {
          return option[0];
        })}
        onChange={(event: any, value: any) => {
          if (config.id === "products" && location.href.includes("addcamera")) {
            createMinReqHelperText(
              setHelperText,
              value,
              config.options,
              navigate
            );
          }
          if (config.id === "products" && location.href.includes("addsite")) {
            if (value.length === 1 && value[0] === "Health Monitoring") {
              localStorage.setItem("healthMonitoringOnly", "true");
            } else {
              localStorage.setItem("healthMonitoringOnly", "false");
            }
          }
          if (
            config.id === "products" &&
            location.href.includes("addsite") &&
            value.includes("Healthcheck")
          ) {
            setHealthCheckSelected(true);
            setActiveWizardContainer(
              <WizardContainer
                handleSubmitData={handleSubmitSite}
                response={responseObject}
                setVisibleScreen={setVisibleScreen}
                responseObject={false}
                choicesChanger={choicesChanger}
                setChoicesChanger={setChoicesChanger}
                setHealthCheckSelected={setHealthCheckSelected}
                healthCheckSelected={true}
                setActiveWizardContainer={setActiveWizardContainer}
                handleSubmitSite={handleSubmitSite}
                setCameraCheckFinsihedAlertOpen={() => {}}
                setGoodCameras={() => {}}
                setConnectivityCameras={() => {}}
                setLowResolutionCameras={() => {}}
                setCameraCheckResultDownloadURL={() => {}}
              />
            );
            if (value.length === 1) {
              localStorage.setItem("healthcheckOnly", "true");
            }
            if (value.length > 1) {
              localStorage.setItem("healthcheckOnly", "false");
            }
          }
          if (config.id === "products" && location.href.includes("addcamera")) {
            setChoiceOptions(computeProductChoiceOptions(value));
          }
          if (
            config.id === "products" &&
            location.href.includes("addsite") &&
            !value.includes("Healthcheck")
          ) {
            setHealthCheckSelected(false);
            localStorage.setItem("healthcheckOnly", "false");
            setActiveWizardContainer(
              <WizardContainer
                handleSubmitData={handleSubmitSite}
                response={responseObject}
                setVisibleScreen={setVisibleScreen}
                responseObject={false}
                choicesChanger={choicesChanger}
                setChoicesChanger={setChoicesChanger}
                setHealthCheckSelected={setHealthCheckSelected}
                healthCheckSelected={false}
                setActiveWizardContainer={setActiveWizardContainer}
                handleSubmitSite={handleSubmitSite}
                setCameraCheckFinsihedAlertOpen={() => {}}
                setGoodCameras={() => {}}
                setConnectivityCameras={() => {}}
                setLowResolutionCameras={() => {}}
                setCameraCheckResultDownloadURL={() => {}}
              />
            );
          }
          const choicesArray: number[] = [];
          value.forEach((element: string) => {
            choicesArray.push(optionsIds.get(element));
          });
          setChoicesChanger(!choicesChanger);
          formik.values[config.field] = choicesArray;
        }}
        sx={{ width: 350 }}
        renderInput={(params) => (
          <Field
            {...params}
            variant="standard"
            defaultValue={formik.values[config.field]}
            label={config.label}
            name={config.id}
            helperText={helperText}
            as={TextFieldComponent}
          />
        )}
      />
    </div>
  );
};

const TextFieldComponent = (props: any) => <TextField {...props} />;
